import React, { useState } from 'react';
import { Button, Space, Input, InputNumber, Form, Select, Row, Col, Tag, Typography, notification, Modal, Popconfirm } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deleteServiceRequest, getListServicesRequest, createServiceRequest, updateServiceRequest } from '../../../services/cms/serviceServiceRequest';
import { listActiveFeaturesRequest } from '../../../services/cms/featureServiceRequest';
import { newObjectKeys } from '../../../utils/utility';

const { Text } = Typography;
const { TextArea } = Input;

const SERVICE_TYPE_OPTIONS = [
  { value: 1, label: 'Dịch vụ 1-1' },
  { value: 2, label: 'Dịch vụ InApp' },
];

const QuanLyDichVu = () => {
  const [formSearch] = Form.useForm();
  const [formEdit] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const { data: dataServices, refetch } = useQuery({
    queryKey: ['dataServices', params],
    queryFn: () => getListServicesRequest(params),
  });

  const { data: featureOptions } = useQuery({
    queryKey: ['activeFeatures'],
    queryFn: () => listActiveFeaturesRequest({}),
  });

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deleteServiceRequest,
    onSuccess: (data) => {
      if (data?.status === 1) { notification.success({ message: 'Xóa dịch vụ thành công!' }); refetch(); }
      else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const { mutate: mutateCreate, isPending: isCreating } = useMutation({
    mutationFn: createServiceRequest,
    onSuccess: (data) => {
      if (data?.status === 1) { notification.success({ message: 'Tạo dịch vụ thành công!' }); setModalOpen(false); formEdit.resetFields(); refetch(); }
      else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const { mutate: mutateUpdate, isPending: isUpdating } = useMutation({
    mutationFn: updateServiceRequest,
    onSuccess: (data) => {
      if (data?.status === 1) { notification.success({ message: 'Cập nhật dịch vụ thành công!' }); setModalOpen(false); setEditingRecord(null); formEdit.resetFields(); refetch(); }
      else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.name) Object.assign(temp, { name: values.name.trim() });
    if (values?.status >= 0) Object.assign(temp, { status: values.status });
    if (values?.service_type > 0) Object.assign(temp, { service_type: values.service_type });
    setParams(temp);
  };

  // Parse comma-separated string IDs to array of integers for Select
  const parseIds = (str) => {
    if (!str) return [];
    return str.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
  };

  const openCreate = () => { formEdit.resetFields(); setEditingRecord(null); setModalOpen(true); };
  const openEdit = (record) => {
    setEditingRecord(record);
    formEdit.setFieldsValue({
      ...record,
      feature_ids: parseIds(record.feature_ids),
      focused_feature_ids: parseIds(record.focused_feature_ids),
    });
    setModalOpen(true);
  };
  const handleModalOk = () => {
    formEdit.validateFields().then(values => {
      // Convert arrays back to comma-separated string
      const payload = {
        ...values,
        feature_ids: values.feature_ids?.join(',') || null,
        focused_feature_ids: values.focused_feature_ids?.join(',') || null,
      };
      if (editingRecord) mutateUpdate({ id: editingRecord.id, ...payload });
      else mutateCreate(payload);
    });
  };

  const featureSelectOptions = (featureOptions || []).map(f => ({
    value: f.id,
    label: f.display_name || f.name,
  }));

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Tên dịch vụ', dataIndex: 'name', key: 'name', width: 150 },
    { title: 'Mô tả', dataIndex: 'description', key: 'description', width: 300, ellipsis: true },
    { title: 'Thứ tự', dataIndex: 'order_place', key: 'order_place' },
    {
      title: 'Loại DV', dataIndex: 'service_type', key: 'service_type',
      render: (v) => v === 1 ? '1-1' : v === 2 ? 'InApp' : '-',
    },
    { title: 'Cấp độ', dataIndex: 'service_level', key: 'service_level' },
    { title: 'Tên cấp độ', dataIndex: 'level_name', key: 'level_name', render: (v) => v || '--' },
    {
      title: 'Trạng thái', dataIndex: 'status', key: 'status',
      render: (status) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? 'Hoạt động' : 'Tắt'}</Tag>,
    },
    {
      title: 'Thao tác', key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => openEdit(record)}><EditOutlined /></Button>
          <Popconfirm title="Xóa dịch vụ này?" onConfirm={() => mutateDelete({ id: record?.id })} okText="Xóa" cancelText="Hủy">
            <Button type="text" danger><DeleteOutlined /></Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Quản lý dịch vụ</h1>
        <Button type="primary" onClick={openCreate}><PlusOutlined /> Thêm dịch vụ</Button>
      </div>
      <Form name="search_services" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="name" label={<Text>Tên dịch vụ</Text>}>
              <Input placeholder="Nhập tên dịch vụ" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select options={[{ value: -1, label: 'Tất cả' }, { value: 1, label: 'Hoạt động' }, { value: 0, label: 'Tắt' }]} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="service_type" label={<Text>Loại dịch vụ</Text>} initialValue={-1}>
              <Select options={[{ value: -1, label: 'Tất cả' }, ...SERVICE_TYPE_OPTIONS]} />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit"><SearchOutlined /> Tìm kiếm</Button>
            {params && Object.values(newObjectKeys(params, ['name', 'status', 'service_type'])).some((value) => value !== undefined && value !== null && value !== '') && (
              <Button htmlType="button" onClick={() => { formSearch.resetFields(); setParams({ page: 1, limit: params.limit }); }}>
                <DeleteOutlined /> Xóa lọc
              </Button>
            )}
          </Col>
        </Row>
      </Form>
      <TableCustom columns={columns} dataSource={dataServices?.data} totalItem={dataServices?.total} params={params} setParams={setParams} />

      <Modal title={editingRecord ? 'Sửa dịch vụ' : 'Thêm dịch vụ'} open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingRecord(null); formEdit.resetFields(); }}
        onOk={handleModalOk} confirmLoading={isCreating || isUpdating} okText="Lưu" cancelText="Hủy" destroyOnClose width={700}>
        <Form form={formEdit} layout="vertical">
          <Form.Item name="name" label="Tên dịch vụ" rules={[{ required: true, message: 'Nhập tên dịch vụ' }]}>
            <Input placeholder="Tên dịch vụ" />
          </Form.Item>

          <Form.Item name="description" label="Mô tả">
            <TextArea rows={3} placeholder="Mô tả dịch vụ" maxLength={500} showCount />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="service_type" label="Loại dịch vụ">
                <Select options={SERVICE_TYPE_OPTIONS} placeholder="Chọn loại dịch vụ" allowClear />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="service_level" label="Cấp độ dịch vụ">
                <InputNumber style={{ width: '100%' }} placeholder="Cấp độ (1, 2, 3...)" min={1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="level_name" label="Tên cấp độ (Level Name)">
                <Input placeholder="VD: Cơ bản, Nâng cao..." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="feature_ids" label="Danh sách tính năng">
            <Select
              mode="multiple" allowClear showSearch placeholder="Chọn tính năng"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={featureSelectOptions}
            />
          </Form.Item>

          <Form.Item name="focused_feature_ids" label="Danh sách tính năng nổi bật">
            <Select
              mode="multiple" allowClear showSearch placeholder="Chọn tính năng nổi bật"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={featureSelectOptions}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="order_place" label="Thứ tự">
                <InputNumber style={{ width: '100%' }} placeholder="Thứ tự" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Trạng thái" initialValue={1}>
                <Select options={[{ value: 1, label: 'Hoạt động' }, { value: 0, label: 'Tắt' }]} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default QuanLyDichVu;

